import { useEffect, useState } from "react";
import scss from "./PlusMinusInput.module.scss";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

/**
 * Number input with styles plus and minus buttons.
 * Handles optimistic UI + backend synchronization.
 */
const PlusMinusInput = ({
    behaviours,
    quantity,
    max,
    setQuantity,
    debounceId = null,
    className,
    translateKey = "cart-default-messages"
}) => {
    const { t } = useTranslation();

    const allow_decimals = behaviours?.display?.quantity_field_allow_decimals ?? false;
    const default_loop_quantity = Number(behaviours?.default_loop_quantity ? (allow_decimals ? behaviours?.default_loop_quantity : Math.ceil(behaviours.default_loop_quantity)) : 1);
    const loop_quantity = Number(behaviours?.loop_quantity ?? default_loop_quantity);

    /**
     * Normalize value → NUMBER (used only for math / API)
     */
    const normalize = (value) => {
        if (value === "" || value === null || value === undefined) return 0;

        const num = Number(value);
        if (Number.isNaN(num)) return 0;

        return allow_decimals
            ? Number(num.toFixed(2))
            : Math.floor(num);
    };

    /**
     * Format value → STRING (used only for input)
     */
    const format = (value) =>
        allow_decimals
            ? normalize(value).toFixed(2)
            : String(Math.floor(value));

    // STATE
    const [tempValue, setTempValue] = useState(() => format(quantity));
    const [displayInputFieldLoader, setDisplayInputFieldLoader] = useState(false);
    const [config, setConfig] = useState({});
    
    /**
     * Backend → UI sync
     */
    useEffect(() => {
        setTempValue(format(quantity));
        setDisplayInputFieldLoader(false);
    }, [quantity]);

    /**
     * Load inventory configuration
     */
    useEffect(() => {
        if (typeof window === "undefined") return;

        const inventory_config = localStorage.getItem("configuration_inventories");
        if (!inventory_config) return;

        const parsed = JSON.parse(inventory_config);
        const configMap = parsed.reduce((acc, item) => {
            acc[item.slug] = item.value;
            return acc;
        }, {});

        setConfig(configMap);
    }, []);

    /**
     * Debounced UI → API sync
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setDisplayInputFieldLoader(true);
            
            let quantity_to_send = buildQuantityToSend(tempValue);

            if(quantity_to_send !== quantity) {
                setQuantity(quantity_to_send);
            } else {
                setDisplayInputFieldLoader(false);
            }
        }, 500);

        return () => clearTimeout(timer)
    }, [tempValue]);

    const buildQuantityToSend = (value) => {
        const normalized = normalize(value);

        if (normalized < default_loop_quantity) {
            return default_loop_quantity;
        }

        // Safe decimal multiple check
        const ratio = normalized / default_loop_quantity;
        const isMultiple = Math.abs(ratio - Math.round(ratio)) < 1e-8;

        if (isMultiple) {
            return normalized;
        }

        const nextStep = Math.ceil(ratio) * default_loop_quantity;
        return normalize(nextStep);
    };

    const handleInputFieldValue = (input_value) => {
        const regex = allow_decimals ? /^\d*\.?\d*$/ : /^\d*$/;
        if (regex.test(input_value)) {
            return input_value;
        }
        return default_loop_quantity;
    };

    const inputQyantityChange = ({ count_type, input_value }) => {
        let next = 0;
        let current_temp = normalize(tempValue);

        switch(count_type) {
            case "add":
                next = current_temp + loop_quantity;
                break;
            case "remove":
                next = current_temp - loop_quantity;
                break;
            default:
                next = handleInputFieldValue(input_value);
                break;
        }

        if (next < default_loop_quantity) return;

        if (
            Boolean(Number(config?.check_requested_inventory_product_quantity)) &&
            next > max
        ) {
            toast.error(t(`${translateKey}-not_enough_stock`));
            next = max;
        }

        setTempValue(format(next));
    }

    /**
     * UI classes
     */
    const isUpdating = Boolean(displayInputFieldLoader && debounceId);
    const wrapperClass = [
            scss.wrapper,
            className,
            isUpdating ? scss.dirty : null
        ]
            .filter(Boolean)
            .join(" ");

    return (
        <div className={wrapperClass} aria-busy={isUpdating}>
            <span
                className={scss.button}
                onClick={() => inputQyantityChange({ count_type: "remove" })}
            >
                -
            </span>

            <input
                id={debounceId}
                maxLength="100"
                value={tempValue}
                onChange={(e) => inputQyantityChange({ count_type: "replace", input_value: e.target.value })}
                onClick={(e) => e.target.select()}
                inputMode="decimal"
            />

            <span
                className={scss.button}
                onClick={() => inputQyantityChange({ count_type: "add" })}
            >
                +
            </span>

        </div>
    );
};

export default PlusMinusInput;

